import { computed, ref } from "vue";

export type DomainWizardStep = 1 | 2 | 3;

export type DomainWizardPhase =
    | "enter-domain"
    | "configure-dns-record"
    | "review-and-confirm";

export type DomainLookupStatus =
    | "idle"
    | "checking"
    | "available"
    | "exists"
    | "error";

export interface IWizardDomain {
    domain: string;
    account?: string;
    active?: boolean;
    dmarcRecord?: string | null;
    created_at?: string | null;
}

const PHASES: Record<DomainWizardStep, DomainWizardPhase> = {
    1: "enter-domain",
    2: "configure-dns-record",
    3: "review-and-confirm",
};

// The wizard's progress state, kept here rather than as a dozen refs inside the
// component: the flow is described in one place, the states derived from it stop
// being restated at every call site, and anything that needs to know where the
// user stands reads it from here instead of the component re-publishing it.
// Module scope holds the one wizard on screen; resetWizardState() restores it
// whenever the wizard mounts.
const currentStep = ref<DomainWizardStep>(1);
const domain = ref("");
const domainCheckStatus = ref<DomainLookupStatus>("idle");
const existingDomain = ref<IWizardDomain | null>(null);
const dnsValidated = ref(false);
const lastLookedUpDomain = ref<string | null>(null);
const txtRecordFromServer = ref<string | null>(null);
const txtRecordPreview = ref("");
const lastPreparedRecord = ref("");

const phase = computed<DomainWizardPhase>(() => PHASES[currentStep.value]);
const domainCheckBusy = computed(() => domainCheckStatus.value === "checking");
const domainApproved = computed(() => domainCheckStatus.value === "available");
const domainExists = computed(() => domainCheckStatus.value === "exists");

// A DNS result only describes the domain it was fetched for, so this compares
// instead of holding a flag: retyping the domain invalidates the result without
// anything having to remember to clear it.
const dnsChecked = computed(
    () =>
        lastLookedUpDomain.value !== null &&
        lastLookedUpDomain.value === domain.value,
);

// Resets progress only. The record preview is derived from the prepared record
// and is seeded by its owner on mount; blanking it here would leave the record
// configurator with an empty value to rebuild from, which drops the rua= tag.
function resetWizardState(): void {
    currentStep.value = 1;
    domain.value = "";
    domainCheckStatus.value = "idle";
    existingDomain.value = null;
    dnsValidated.value = false;
    lastLookedUpDomain.value = null;
    txtRecordFromServer.value = null;
}

export function useDomainWizardState() {
    return {
        currentStep,
        domain,
        domainCheckStatus,
        existingDomain,
        dnsValidated,
        lastLookedUpDomain,
        txtRecordFromServer,
        txtRecordPreview,
        lastPreparedRecord,
        phase,
        domainCheckBusy,
        domainApproved,
        domainExists,
        dnsChecked,
        resetWizardState,
    };
}
